/**
 * Login Page Component
 * Supports email/password and Google OAuth login via Supabase
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import ShieldIcon from '../ShieldIcon';
import { prefetchForDashboard, prefetchDashboardModules } from '../../utils/prefetch';
import { resolvePostLoginPath } from '../../lib/postLoginRedirect';

const LoginPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { signIn, signInWithGoogle, isAuthenticated, isLoading, error, clearError } = useAuth();
    const hasPrefetchedRef = useRef(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Get redirect path from URL query (?next=) or location state (e.g. ProtectedRoute)
    const nextParam = searchParams.get('next');
    const from = resolvePostLoginPath(nextParam, (location.state as { from?: { pathname?: string } } | null)?.from?.pathname);

    // Prefetch dashboard modules when login page mounts
    useEffect(() => {
        prefetchForDashboard();
    }, []);

    // Aggressively prefetch when user starts typing (they're likely about to log in)
    useEffect(() => {
        if ((email || password) && !hasPrefetchedRef.current) {
            hasPrefetchedRef.current = true;
            // Immediately prefetch without idle callback since user is actively engaging
            prefetchDashboardModules();
        }
    }, [email, password]);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, from]);

    // Clear errors on input change
    useEffect(() => {
        if (error) clearError();
        setLocalError(null);
    }, [email, password]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        // Basic validation
        if (!email || !password) {
            setLocalError(t('auth.errors.allFieldsRequired'));
            return;
        }

        setIsSubmitting(true);
        try {
            await signIn(email, password);
            navigate(from, { replace: true });
        } catch (err) {
            // Error is already set in context
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLocalError(null);
        try {
            const redirectTo = nextParam
                ? `${window.location.origin}${resolvePostLoginPath(nextParam, undefined)}`
                : undefined;
            await signInWithGoogle(redirectTo);
        } catch (err) {
            // Error is handled in context
        }
    };

    const displayError = localError || error;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                {/* Logo and Title */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center justify-center group">
                        <div className="p-3 rounded-2xl bg-white dark:bg-secondary-800 shadow-lg group-hover:shadow-xl transition-shadow duration-200">
                            <ShieldIcon className="h-12 w-12" />
                        </div>
                    </Link>
                    <h2 className="mt-6 text-3xl font-bold text-secondary-800 dark:text-secondary-100">
                        {t('auth.login.title')}
                    </h2>
                    <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                        {t('auth.login.subtitle')}{' '}
                        <Link
                            to={nextParam ? `/register?next=${encodeURIComponent(nextParam)}` : '/register'}
                            className="font-medium text-brand-500 dark:text-brand-400 hover:text-brand-600 dark:hover:text-brand-300 transition-colors"
                        >
                            {t('auth.login.createAccount')}
                        </Link>
                    </p>
                </div>

                {/* Login Form */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-6 sm:p-8">
                    {displayError && (
                        <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-700 rounded-xl flex items-start">
                            <AlertCircle className="h-5 w-5 text-danger-600 dark:text-danger-400 mr-3 flex-shrink-0 mt-0.5" aria-hidden />
                            <span className="text-sm text-danger-800 dark:text-danger-200">{displayError}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email Input */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                {t('auth.fields.email')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="block w-full pl-10 pr-4 py-3 border border-secondary-300 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                {t('auth.fields.password')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-secondary-400 dark:text-secondary-500" />
                                </div>
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-12 py-3 border border-secondary-300 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting || isLoading}
                            className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl text-white bg-brand-500 dark:bg-brand-600 hover:bg-brand-600 dark:hover:bg-brand-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                    {t('auth.login.signingIn')}
                                </>
                            ) : (
                                t('auth.login.signIn')
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-secondary-200 dark:border-secondary-600" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                                    {t('auth.login.orContinueWith')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Google Login Button */}
                    <div className="mt-6">
                        <button
                            onClick={handleGoogleLogin}
                            disabled={isSubmitting || isLoading}
                            className="w-full flex items-center justify-center py-3 px-4 border border-secondary-300 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors disabled:opacity-50"
                        >
                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                                <path
                                    fill="#4285F4"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="#34A853"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="#FBBC05"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="#EA4335"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Continue with Google</span>
                        </button>
                    </div>
                </div>

                {/* Footer Links */}
                <div className="mt-6 text-center">
                    <Link to="/privacy" className="text-sm text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 transition-colors">
                        {t('auth.privacyPolicy')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
